import {
  type BalanceFilter,
  ChainType,
  calculateTotalBalance,
  filterBalancesByChains,
  filterBalancesSummary,
  filterWalletGroupedAccounts,
} from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { sum } from 'remeda'
import { useUserSettings } from '@/src/hooks/use-user-settings'
import { balancesStreamQuery } from '@/src/queries/keys/balances'
import { accountsAddressesAtom } from '@/src/stores/accounts/atoms'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import {
  balancesByChainsAtom,
  balancesSummaryAtom,
} from '@/src/stores/balances/atom'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import { walletKeyringAtom } from '@/src/stores/wallets/atoms'

// #region Filtering

/**
 * Grouped balances with summary of common metadata, filtered by chain ID, strings search query, and min balance filter.
 * Includes user settings hide small balances filter by default, but is overrideable by setting `minValueUsd` prop.
 */
export function useFilteredBalances(filter_: BalanceFilter = {}) {
  const unfilteredBalancesSummary = useAtomValue(balancesSummaryAtom)
  const unfilteredBalancesByChains = useAtomValue(balancesByChainsAtom)
  const { groupedAccounts } = useAtomValue(walletAccountsAtom)

  const { userSettings } = useUserSettings()

  // Access each filter prop individually to avoid unnecessary re-renders yet allow passing an object to the hook.
  const memoedFilter: BalanceFilter = useMemo(() => {
    return {
      minValueUsd: filter_.minValueUsd,
      chainId: filter_.chainId,
      query: filter_.query,
      denom: filter_.denom,
      ticker: filter_.ticker,
      tickers: filter_.tickers,
      addresses: filter_.addresses,
    }
  }, [
    filter_.minValueUsd,
    filter_.chainId,
    filter_.query,
    filter_.denom,
    filter_.ticker,
    filter_.tickers,
    filter_.addresses,
  ])

  const filter: BalanceFilter = useMemo(
    () => ({
      ...memoedFilter,
      minValueUsd:
        filter_.minValueUsd ??
        (userSettings.hideSmallBalances
          ? userSettings.smallestBalanceUsd
          : undefined),
    }),
    [
      memoedFilter,
      filter_.minValueUsd,
      userSettings.hideSmallBalances,
      userSettings.smallestBalanceUsd,
    ],
  )

  const balancesByChains = useMemo(
    () => filterBalancesByChains(unfilteredBalancesByChains, filter),
    [unfilteredBalancesByChains, filter],
  )

  const balancesSummary = useMemo(
    () => filterBalancesSummary(unfilteredBalancesSummary, filter),
    [unfilteredBalancesSummary, filter],
  )

  const privacyBalancesSummary = useMemo(
    () =>
      filterBalancesSummary(unfilteredBalancesSummary, {
        ...filter,
        chainType: ChainType.Namada,
      }),
    [unfilteredBalancesSummary, filter],
  )

  const filteredGroupedAccounts = useMemo(
    () => filterWalletGroupedAccounts(groupedAccounts, filter),
    [groupedAccounts, filter],
  )

  const totalBalance = useMemo(
    () => calculateTotalBalance(balancesSummary),
    [balancesSummary],
  )

  return {
    balancesSummary,
    privacyBalancesSummary,
    balancesByChains,
    groupedAccounts: filteredGroupedAccounts,
    totalBalance,
  }
}

/** Includes default filter to only include balances in their Polaris accounts/portfolio. */
export function useAccountsFilteredBalances(
  filter: BalanceFilter & { includeWalletKeyring?: boolean } = {},
) {
  const { privacy } = useAtomValue(featureFlagsAtom)
  const { data: mpcWalletAddresses } = useAtomValue(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )
  const walletKeyring = useAtomValue(walletKeyringAtom)
  const accountsAddresses = useAtomValue(accountsAddressesAtom)

  /** Reminder: addresses list is a whitelist w/r/t filtering. */
  const addresses = useMemo(() => {
    const _addresses = new Set<string>(accountsAddresses)

    if (filter.addresses) {
      filter.addresses.forEach((addr) => _addresses.add(addr))
    }

    if (privacy && mpcWalletAddresses?.namada) {
      _addresses.add(mpcWalletAddresses.namada)
    }

    if (filter.includeWalletKeyring) {
      Object.values(walletKeyring.addresses).forEach((addr) =>
        _addresses.add(addr),
      )
    }

    return Array.from(_addresses)
  }, [
    accountsAddresses,
    filter.addresses,
    privacy,
    mpcWalletAddresses?.namada,
    filter.includeWalletKeyring,
    walletKeyring,
  ])

  return useFilteredBalances({
    ...filter,
    addresses,
  })
}

// #region Streaming

/** If an address is provided, will stream balances and update the query client. */
export function useBalances(address?: string, enabled = true) {
  return useQuery({
    ...balancesStreamQuery(address),
    select: (data) => {
      // Handle undefined/null data defensively
      if (!data) {
        return { balances: [], valueUsd: 0 }
      }
      return {
        balances: data,
        valueUsd: data.reduce(
          (total, { balances }) =>
            total + sum(balances.map(({ value_usd }) => value_usd ?? 0)),
          0,
        ),
      }
    },
    enabled,
  })
}
